import { ROUTERS } from '@/constant';
import { DeviceType, getDeviceType } from '@/utils/mobile.util';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type UseDeviceTypeRedirectReturn = {
	isRedirecting: boolean;
	currentDeviceType: DeviceType;
};

/**
 * Hook to handle device type changes and redirect accordingly.
 * @param currentPage - 'mobile' | 'tablet' - the expected device type for current page
 * @returns Object with isRedirecting flag to prevent rendering during redirect
 */
export const useDeviceTypeRedirect = (
	currentPage: 'mobile' | 'tablet'
): UseDeviceTypeRedirectReturn => {
	const navigate = useNavigate();
	const lastDeviceType = useRef<DeviceType>(getDeviceType());
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [currentDeviceType, setCurrentDeviceType] =
		useState<DeviceType>(getDeviceType);

	// Redirect logic - shared between mount check and resize handler
	const performRedirect = (newDeviceType: DeviceType) => {
		if (currentPage === 'mobile') {
			// Currently on mobile page - redirect if not mobile
			if (newDeviceType === 'tablet') {
				setIsRedirecting(true);
				navigate(ROUTERS.TAB_01, { replace: true });
				return true;
			} else if (newDeviceType === 'pc') {
				setIsRedirecting(true);
				navigate('/', { replace: true });
				return true;
			}
		} else if (currentPage === 'tablet') {
			// Currently on tablet page - redirect if not tablet
			if (newDeviceType === 'mobile') {
				setIsRedirecting(true);
				navigate(ROUTERS.MOB_01, { replace: true });
				return true;
			} else if (newDeviceType === 'pc') {
				setIsRedirecting(true);
				navigate('/', { replace: true });
				return true;
			}
		}
		return false;
	};

	// Immediate check on mount - redirect if device type doesn't match page
	useEffect(() => {
		const deviceType = getDeviceType();
		setCurrentDeviceType(deviceType);
		lastDeviceType.current = deviceType;

		// If device type doesn't match expected page, redirect immediately
		if (
			(currentPage === 'mobile' && deviceType !== 'mobile') ||
			(currentPage === 'tablet' && deviceType !== 'tablet')
		) {
			performRedirect(deviceType);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only on mount

	// Listen for resize events
	useEffect(() => {
		let debounceTimer: ReturnType<typeof setTimeout>;

		const handleResize = () => {
			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(() => {
				const newDeviceType = getDeviceType();
				setCurrentDeviceType(newDeviceType);

				// Only redirect if device type actually changed
				if (newDeviceType === lastDeviceType.current) {
					return;
				}

				lastDeviceType.current = newDeviceType;
				performRedirect(newDeviceType);
			}, 150); // Debounce 150ms
		};

		window.addEventListener('resize', handleResize);
		return () => {
			clearTimeout(debounceTimer);
			window.removeEventListener('resize', handleResize);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate, currentPage]);

	return { isRedirecting, currentDeviceType };
};

export default useDeviceTypeRedirect;
